import React from "react";
import { Alert, Box, Checkbox, CircularProgress, FormControlLabel, Typography } from "@mui/material";
import { normalizeIdArray } from "../utils/companyUtils";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

function resolveUrl(pathOrUrl) {
    if (!pathOrUrl) return "";
    const value = String(pathOrUrl).trim();
    if (/^https?:\/\//i.test(value)) return value;
    return `${API_BASE}${value.startsWith("/") ? value : `/${value}`}`;
}

export default function SectionLogos({
    form,
    setForm,
    logos,
    logosLoading,
    logosError,
}) {
    const activeLogos = React.useMemo(
        () => (logos || []).filter((logo) => logo.is_active === undefined || !!logo.is_active),
        [logos]
    );

    const selectedIds = normalizeIdArray(form.logo_ids);

    const toggleLogo = (id) => {
        setForm((f) => {
            const ids = new Set(normalizeIdArray(f.logo_ids));
            if (ids.has(id)) ids.delete(id);
            else ids.add(id);
            return { ...f, logo_ids: Array.from(ids).sort((a, b) => a - b) };
        });
    };

    if (logosError) return <Alert severity="error">{logosError}</Alert>;

    if (logosLoading) {
        return (
            <Box sx={{ py: 2, textAlign: "center" }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (!activeLogos.length) {
        return <Alert severity="info">Keine Logos vorhanden. Lege zuerst Logos in der Logo-Verwaltung an.</Alert>;
    }

    return (
        <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                {selectedIds.length} von {activeLogos.length} ausgewählt
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: 1,
                }}
            >
                {activeLogos.map((logo) => {
                    const id = Number(logo.id);
                    const checked = selectedIds.includes(id);
                    const url = resolveUrl(logo.url);
                    return (
                        <FormControlLabel
                            key={logo.id}
                            control={
                                <Checkbox
                                    size="small"
                                    checked={checked}
                                    onChange={() => toggleLogo(id)}
                                    sx={{ alignSelf: "flex-start", mt: 0.25 }}
                                />
                            }
                            label={
                                <Box sx={{ minWidth: 0 }}>
                                    <Box
                                        sx={{
                                            height: 52,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "flex-start",
                                            mb: 0.5,
                                        }}
                                    >
                                        {url ? (
                                            <Box
                                                component="img"
                                                src={url}
                                                alt={logo.label_de || logo.name}
                                                sx={{ maxWidth: 130, maxHeight: 48, objectFit: "contain" }}
                                            />
                                        ) : (
                                            <Typography variant="caption" color="text.secondary">
                                                Kein Bild hochgeladen
                                            </Typography>
                                        )}
                                    </Box>
                                    <Typography variant="body2" noWrap>
                                        {logo.label_de || logo.name}
                                    </Typography>
                                </Box>
                            }
                            sx={{
                                m: 0,
                                minWidth: 0,
                                alignItems: "flex-start",
                                borderRadius: 1,
                                px: 1,
                                py: 0.75,
                                border: "1px solid",
                                borderColor: checked ? "rgba(240, 172, 77, 0.55)" : "divider",
                                bgcolor: checked ? "rgba(240, 172, 77, 0.10)" : "transparent",
                                "&:hover": { bgcolor: "action.hover" },
                            }}
                        />
                    );
                })}
            </Box>
        </Box>
    );
}
